/**
 * Bootleg Biology (Nashville) — wild isolates, mixed cultures and a small
 * set of clean "Bootleg Classic" strains.
 *
 * Temperatures are °C, converted from the lab's published °F ranges and
 * rounded to the nearest whole degree. Attenuation and flocculation follow
 * the brewing-info blocks on each product page; STA1 is taken from the
 * lab's own non-STA1 / diastatic notes where they print them, otherwise
 * left `null`. Alcohol tolerance is almost never published as a number.
 */

import type { Yeast } from '../../schema/yeast';

/** Bootleg Biology cultures. @see Yeast */
export const bootlegBiologyYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // Clean and hop-forward ales
  // -------------------------------------------------------------------------
  {
    id: 'bootleg-classic-american',
    lab: 'Bootleg Biology',
    productCode: 'BBUSA1',
    name: 'Classic American',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      "Bootleg's Chico culture: malt-forward, hop-sticky and bitter rather than pillowy, which is exactly what a West Coast IPA or American pale ale wants. Low pH tolerance also makes it a reliable closer after a kettle sour.",
    tags: ['clean', 'american', 'chico', 'ipa', 'west-coast', 'pale-ale'],
    url: 'https://bootlegbiology.com/product/classic-american/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bootleg-classic-english',
    lab: 'Bootleg Biology',
    productCode: 'BBENG1',
    name: 'Classic English',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'An ester-driven English ale strain that reads juicy and tropical rather than merely bready. Unusual among English cultures for staying in suspension, so it can double as a hazy house yeast if you keep the dry hop generous.',
    tags: ['english', 'esters', 'tropical', 'hazy', 'pale-ale'],
    url: 'https://bootlegbiology.com/product/classic-english/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bootleg-classic-new-england',
    lab: 'Bootleg Biology',
    productCode: 'BBNE',
    name: 'Classic New England',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A Vermont-lineage New England strain: peach and citrus esters, a soft body and a haze that survives the cold crash. The straightforward pick when the beer is supposed to taste of juice rather than of bitterness.',
    tags: ['hazy', 'new-england', 'ipa', 'stone-fruit', 'esters'],
    url: 'https://bootlegbiology.com/product/classic-new-england/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-neepah',
    lab: 'Bootleg Biology',
    productCode: 'BBXNPA',
    name: 'NEEPAH Blend',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A fruit-forward Saccharomyces blend built for hazy IPA: huge citrus and mango esters that make tropical hops read juicier than the same bill with a neutral yeast. Stays hazy without tasting yeasty; bump the temperature a couple of degrees after two days.',
    tags: ['hazy', 'ipa', 'tropical', 'mango', 'citrus', 'blend'],
    url: 'https://bootlegbiology.com/product/neepah-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bootleg-double-neepah',
    lab: 'Bootleg Biology',
    productCode: 'BBXDNPA',
    name: 'Double NEEPAH V2',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 84 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A louder, drier revision of NEEPAH aimed at double IPA: the same mango-citrus blast with enough attenuation that an 8% beer does not finish syrupy. Still a haze machine; still a blend, so harvests are a mixed culture.',
    tags: ['hazy', 'dipa', 'ipa', 'tropical', 'blend', 'dry-finish'],
    url: 'https://bootlegbiology.com/product/double-neepah-v2/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-ob-belgian',
    lab: 'Bootleg Biology',
    productCode: 'BBOB',
    name: 'OB Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 18, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Belgian abbey-style strain throwing dark fruit and a restrained clove phenol, happy from blonde through quadrupel. Start cool and let it free-rise; pitching hot turns the esters solventy in a hurry.',
    tags: ['belgian', 'abbey', 'phenolic', 'dubbel', 'tripel', 'esters'],
    url: 'https://bootlegbiology.com/product/ob-belgian-yeast/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-mustange-kolsch',
    lab: 'Bootleg Biology',
    productCode: 'BBKOLSCH',
    name: 'Mustange Kölsch',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 13, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A kölsch hybrid isolated for a clean, faintly winey profile at ale-cool temperatures. Cold condition it after primary; the beer is not finished until it has sat bright and still.',
    tags: ['kolsch', 'hybrid', 'clean', 'german', 'crisp'],
    url: 'https://bootlegbiology.com/product/mustange-kolsch/',
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Kveik-family
  // -------------------------------------------------------------------------
  {
    id: 'bootleg-oslo',
    lab: 'Bootleg Biology',
    productCode: 'BBOSL',
    name: 'OSLO',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 76, max: 86 },
    temperatureC: { min: 29, max: 37 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A kveik-family bottom fermenter isolated from Eik & Tid in Oslo: clean and lager-like even at 37 °C, with no phenols and no fusel heat. Pitch and hold near 35 °C for a three-day finish; cooler pitches can stall or flocculate out early.',
    tags: ['kveik', 'clean', 'pseudo-lager', 'fast', 'norwegian', 'oslo'],
    url: 'https://bootlegbiology.com/product/oslo/',
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // Lagers
  // -------------------------------------------------------------------------
  {
    id: 'bootleg-arl-southern-lager',
    lab: 'Bootleg Biology',
    productCode: 'BBARL',
    name: 'ARL The Southern Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 88, max: 93 },
    temperatureC: { min: 13, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      "Bootleg's first banked culture and still their flagship lager: extremely dry, clean in the low teens °C and faintly citric if you push it toward 20 °C. Sulfur during primary blows off in a couple of weeks; attenuation this high leaves very little body, so mash warm if you need it.",
    tags: ['lager', 'clean', 'dry', 'southern', 'pilsner', 'cold-ipa'],
    url: 'https://bootlegbiology.com/product/arl/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bootleg-regal-lager',
    lab: 'Bootleg Biology',
    productCode: 'BBREGAL',
    name: 'REGAL Lager Blend',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 80, max: 87 },
    temperatureC: { min: 10, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A lager blend designed to start cold and finish hot: pitch at 10–13 °C for three days, then ramp toward 30 °C to knock out the rest. Rich malt, little fruit, and a much shorter tank residency than a single-strain 34/70 pitch.',
    tags: ['lager', 'blend', 'fast', 'malty', 'clean', 'german'],
    url: 'https://bootlegbiology.com/product/regal-lager-yeast-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bootleg-czech-unpasteurized-lager',
    lab: 'Bootleg Biology',
    productCode: 'BBCUL',
    name: 'Czech Unpasteurized Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A Czech pilsner culture isolated from unpasteurized lager: fuller and maltier than a German 34/70 type, with the sulfur that a proper Bohemian pilsner is supposed to blow off during a long cold rest.',
    tags: ['lager', 'czech', 'pilsner', 'malty', 'traditional'],
    url: 'https://bootlegbiology.com/product/czech-unpasteurized-lager/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-franconian-keller',
    lab: 'Bootleg Biology',
    productCode: 'BBFKL',
    name: 'Franconian Keller Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A kellerbier-leaning Franconian lager strain that stays a little cloudy and a little malty, with a rounder finish than a pilsner yeast. Skip the long polish if you want it to taste like a Bamberg taproom pour.',
    tags: ['lager', 'kellerbier', 'franconian', 'german', 'malty', 'hazy'],
    url: 'https://bootlegbiology.com/product/franconian-keller-lager/',
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Wild, Brett and mixed
  // -------------------------------------------------------------------------
  {
    id: 'bootleg-funk-weapon-1',
    lab: 'Bootleg Biology',
    productCode: 'BBFW1',
    name: 'Funk Weapon 1',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Brettanomyces isolate from the Funk Weapon series: earthy, lightly barnyard and slow. Use it in secondary on a finished beer and give it months, not weeks; attenuation here is class-typical of a Brett that is allowed to keep eating.',
    tags: ['brett', 'wild', 'funk', 'barnyard', 'secondary', 'mixed-fermentation'],
    url: 'https://bootlegbiology.com/product/funk-weapon-1/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-funk-weapon-2',
    lab: 'Bootleg Biology',
    productCode: 'BBFW2',
    name: 'Funk Weapon 2',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Brettanomyces bruxellensis throwing ripe tropical fruit rather than horse blanket. Happier as a primary or co-pitch than Weapon 1; still a long-haul organism that will keep drying a beer out in package if you bottle too soon.',
    tags: ['brett', 'wild', 'tropical', 'fruit', 'bruxellensis', 'mixed-fermentation'],
    url: 'https://bootlegbiology.com/product/funk-weapon-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bootleg-funk-weapon-3',
    lab: 'Bootleg Biology',
    productCode: 'BBFW3',
    name: 'Funk Weapon 3',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The funkiest of the Weapon trio: leather, earth and a sharper acidity than the fruit-leaning #2. A secondary organism for lambic-inspired beers, not a three-day house Brett.',
    tags: ['brett', 'wild', 'funk', 'lambic', 'earthy', 'mixed-fermentation'],
    url: 'https://bootlegbiology.com/product/funk-weapon-3/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-sour-weapon-l',
    lab: 'Bootleg Biology',
    productCode: 'BBSWL',
    name: 'Sour Weapon L',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 30, max: 40 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Lactobacillus plantarum blend for kettle souring: fast lactic acid, little acetic bite, and no alcohol of its own. Hold the wort hot, knock it back once pH is in range, then pitch a real yeast. Attenuation here is nominal.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'plantarum', 'gose', 'berliner-weisse'],
    url: 'https://bootlegbiology.com/product/sour-weapon-l-lactobacillus-plantarum-blend/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-sour-weapon-p',
    lab: 'Bootleg Biology',
    productCode: 'BBSWP',
    name: 'Sour Weapon P',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Pediococcus pentosaceus for mixed-fermentation acidity that develops over months rather than hours. Not a kettle-souring bug — it belongs in a solera or a barrel alongside Brett, and it can throw diacetyl on the way to lactic acid.',
    tags: ['sour', 'pediococcus', 'mixed-fermentation', 'barrel', 'solera', 'wild'],
    url: 'https://bootlegbiology.com/product/sour-weapon-pediococcus-pentosaceus-blend/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-sour-solera',
    lab: 'Bootleg Biology',
    productCode: 'BBSOLERA',
    name: 'Sour Solera Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 100 },
    temperatureC: { min: 18, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A yearly mixed-culture dump of Saccharomyces, Brettanomyces, Lactobacillus and Pediococcus meant to inoculate a solera. Expect a dry, tart, slowly evolving beer; do not treat the vintage as a stable house pitch without re-banking it.',
    tags: ['sour', 'solera', 'mixed-fermentation', 'brett', 'lactobacillus', 'wild'],
    url: 'https://bootlegbiology.com/product/sour-solera-blend-seasonal/',
    provenance: 'representative',
  },
  {
    id: 'bootleg-mad-fermentationist-saison',
    lab: 'Bootleg Biology',
    productCode: 'BBXMAD1',
    name: 'Mad Fermentationist Saison Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 90, max: 100 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "Michael Tonsmeire's saison blend: Saccharomyces, wild yeast, Brett and Lactobacillus in one pitch. Cool ferments stay relatively clean; 27 °C brings pepper, clove, citrus and a stone-fruit Brett note, finishing bone dry and tart.",
    tags: ['saison', 'blend', 'brett', 'sour', 'farmhouse', 'mixed-fermentation'],
    url: 'https://bootlegbiology.com/product/the-mad-fermentationist-saison-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bootleg-saison-parfait',
    lab: 'Bootleg Biology',
    productCode: 'BBPARFAIT',
    name: 'Saison Parfait',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 92 },
    temperatureC: { min: 20, max: 28 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A New World saison blend: peppery and citric with enough attenuation to finish a hoppy farmhouse ale without the Dupont stall. Diastatic, so keep it off shared packaging lines and confirm gravity before you bottle.',
    tags: ['saison', 'farmhouse', 'phenolic', 'diastatic', 'blend', 'hop-forward'],
    url: 'https://bootlegbiology.com/product/saison-parfait-new-world-saison-blend/',
    provenance: 'representative',
  },
];
